import { Button, Stack, Typography } from '@mui/material';

import { ALLOW_SLIDER_ITEMS_PREFETCH } from '@/constants/prefetch';

import { FC } from 'react';
import { FilmBanner } from '@/logic/services';

import Link from 'next/link';
import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';
import ImageBoxFuture from '../shared/image-box-future';

const ClockIcon = '/icons/movies-banner/clock.svg';

interface SliderFilmInfoProps {
  primaryColor: string;
  secondaryColor: string;
  filmBanner: FilmBanner;
}

export const SliderFilmInfo: FC<SliderFilmInfoProps> = ({
  primaryColor,
  secondaryColor,
  filmBanner,
}) => {
  const { cityId } = useCity();

  const { title, uniqueName, genres, runtime, rate, movieLang } = filmBanner.film;

  let genresWithDots = [];
  let infosWithDots = [];

  // TODO: replace this function with function that abo omar made, plz 🙏
  const timeConvert = (runtime: number) => {
    const num = runtime;
    const hours = num / 60;
    const roundedHours = Math.floor(hours) ?? 0;
    const minutes = (hours - roundedHours) * 60;
    const roundedMinutes = Math.round(minutes) ?? 0;

    const filmFormattedDuration = roundedHours
      ? `${roundedHours}h ${roundedMinutes}m`
      : roundedMinutes
      ? `${roundedMinutes}m`
      : '';

    return filmFormattedDuration;
  };

  genresWithDots = genres
    .slice(0, 2)
    .map(genre => [
      <Typography
        key={genre.id}
        component='span'
        sx={{
          fontSize: pxToRem(24),
          letterSpacing: pxToRem(0.1),
        }}
      >
        {genre.name}
      </Typography>,
      <Typography
        key={genre.id.toString().concat('Dot')}
        component='span'
        sx={{
          marginX: pxToRem(4),
        }}
      >
        &bull;
      </Typography>,
    ])
    .flat();
  genresWithDots.pop();

  if (runtime) {
    infosWithDots.push(timeConvert(runtime));
  }
  if (rate) {
    infosWithDots.push(rate.title);
  }
  if (movieLang) {
    infosWithDots.push(movieLang);
  }

  infosWithDots = infosWithDots
    .map((info, index) => [
      <Typography
        key={index}
        component='span'
        sx={{
          fontSize: pxToRem(24),
          letterSpacing: pxToRem(0.1),
        }}
      >
        {info}
      </Typography>,
      <Typography
        key={index.toString().concat('Dot')}
        component='span'
        sx={{
          marginX: pxToRem(4),
        }}
      >
        &bull;
      </Typography>,
    ])
    .flat();
  infosWithDots.pop();

  return (
    <>
      <Typography
        sx={{
          fontWeight: 700,
          fontFamily: 'co-head-line-corp-bold',
          lineHeight: pxToRem(35.13),
          color: primaryColor,
          marginBottom: pxToRem(24),
          marginX: {
            mobile: '32px',
            laptop: '0px',
          },
          fontSize: {
            tablet: pxToRem(30),
            mobile: '22px',
          },
          textAlign: {
            mobile: 'center',
            laptop: 'start',
          },
        }}
      >
        {title}
      </Typography>

      <Stack
        direction='row'
        sx={{
          alignItems: 'center',
          color: secondaryColor,
        }}
      >
        <ImageBoxFuture
          img={ClockIcon}
          sx={{
            width: {
              mobile: '24px',
              tablet: pxToRem(28),
            },
            height: {
              mobile: '24px',
              tablet: pxToRem(28),
            },
            marginRight: pxToRem(4),
          }}
        />
        {infosWithDots}
      </Stack>
      <Stack
        direction='row'
        sx={{
          marginTop: pxToRem(16),
          alignItems: 'center',
          color: secondaryColor,
        }}
      >
        {genresWithDots}
      </Stack>

      <Link
        href={{
          pathname: `/movies/${uniqueName}`,
          // A city-less visitor (SSR, or before the city dialog is answered)
          // used to mint `?cityId=` with an empty value here. That is the shape
          // that reaches the films API as an empty/`null` cityId, and it is now
          // in the SSR HTML, so crawlers index the query form of every film URL.
          query: {
            ...(cityId && { cityId }),
          },
        }}
        legacyBehavior
        passHref
        prefetch={ALLOW_SLIDER_ITEMS_PREFETCH}
      >
        <Button
          id={`slider-${filmBanner.id}-btn`}
          variant='contained'
          component='a'
          sx={{
            marginTop: pxToRem(16),
            paddingX: pxToRem(49),
            paddingY: pxToRem(18),
            fontFamily: 'co-head-line-corp-bold',
            borderRadius: pxToRem(16),
            height: { tablet: pxToRem(56), mobile: '56px' },
            fontSize: {
              mobile: '16px',
              tablet: pxToRem(16),
            },
            fontWeight: '700',
          }}
        >
          {filmBanner.buttonText}
        </Button>
      </Link>
    </>
  );
};
